// src/services/bookmarkService.js
import { generateClient } from 'aws-amplify/api';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// GraphQL operations
const TOGGLE_BOOKMARK = `
  mutation ToggleBookmark($postID: ID!) {
    toggleBookmark(postID: $postID) {
      id
      postID
      userID
      notes
      tags
      createdAt
      updatedAt
    }
  }
`;

const LIST_USER_BOOKMARKS = `
  query ListUserBookmarks($userID: ID!, $limit: Int, $nextToken: String) {
    listBookmarks(
      filter: { userID: { eq: $userID } }
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        postID
        reviewID
        userID
        contentType
        reviewType
        notes
        tags
        createdAt
        updatedAt
        post {
          id
          content
          images
          authorID
          createdAt
          author {
            id
            displayName
            username
            avatar
          }
        }
      }
      nextToken
    }
  }
`;

const UPDATE_BOOKMARK = `
  mutation UpdateBookmark($input: UpdateBookmarkInput!) {
    updateBookmark(input: $input) {
      id
      postID
      userID
      notes
      tags
      createdAt
      updatedAt
    }
  }
`;

export const bookmarkService = {
  // Toggle bookmark for a post
  async toggleBookmark(postID) {
    try {
      const response = await getClient().graphql({
        query: TOGGLE_BOOKMARK,
        variables: { postID }
      });
      return response.data.toggleBookmark;
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      throw error;
    }
  },

  // Get user's bookmarks
  async getUserBookmarks(userID, limit = 20, nextToken = null) {
    try {
      const response = await getClient().graphql({
        query: LIST_USER_BOOKMARKS,
        variables: { userID, limit, nextToken }
      });
      return response.data.listBookmarks;
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      throw error;
    }
  },

  // Update bookmark notes/tags
  async updateBookmark(id, notes, tags) {
    try {
      const response = await getClient().graphql({
        query: UPDATE_BOOKMARK,
        variables: {
          input: { id, notes, tags }
        }
      });
      return response.data.updateBookmark;
    } catch (error) {
      console.error('Error updating bookmark:', error);
      throw error;
    }
  },

  // Check if post is bookmarked by user
  async isPostBookmarked(postID, userID) {
    try {
      const response = await getClient().graphql({
        query: `
          query CheckBookmark($postID: ID!, $userID: ID!) {
            listBookmarks(
              filter: { 
                and: [
                  { postID: { eq: $postID } },
                  { userID: { eq: $userID } },
                  { contentType: { eq: "POST" } }
                ]
              }
              limit: 1
            ) {
              items {
                id
              }
            }
          }
        `,
        variables: { postID, userID }
      });
      return response.data.listBookmarks.items.length > 0;
    } catch (error) {
      console.error('Error checking bookmark status:', error);
      return false;
    }
  },

  // Check if review is bookmarked by user
  async isReviewBookmarked(reviewID, userID) {
    try {
      const response = await getClient().graphql({
        query: `
          query CheckReviewBookmark($reviewID: ID!, $userID: ID!) {
            listBookmarks(
              filter: { 
                and: [
                  { reviewID: { eq: $reviewID } },
                  { userID: { eq: $userID } },
                  { contentType: { eq: "REVIEW" } }
                ]
              }
              limit: 1
            ) {
              items {
                id
              }
            }
          }
        `,
        variables: { reviewID, userID }
      });
      return response.data.listBookmarks.items.length > 0;
    } catch (error) {
      console.error('Error checking review bookmark status:', error);
      return false;
    }
  }
};